import React, { useState, useRef, useEffect } from "react";
import {
  List,
  Box,
  Button,
  Typography,
  Divider,
  Card,
  Grid,
  Stack,
  FormControlLabel,
  TextField,
  Select,
  OutlinedInput,
  MenuItem,
} from "@mui/material";

import { useResponsive } from "../../../hooks/useResponsive";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteCard,
  fetchPaymentToken,
  makeACHPayment,
  makePayment,
} from "../../../redux/payments/action";
import { cancelReservationRequest } from "../../../redux/reservations/action";

import moment from "moment-timezone";
import { MOMENT_CONSTANTS } from "../../../utils/momentUtils";
import {
  ACH_PAYMENT_COUNTRIES,
  ACH_PAYMENT_SCHEMNA,
  getPaymentType,
  initializeBraintree,
  PAYMENT_TYPE,
} from "./utils";
import storePersist from "../../../services/localStorage";
import { localConstant } from "../../../services/localStorage";

import MenuBar from "../../menuBar";
import NavBar from "../../../NavBar";
import CustomAlert from "../../../components/CustomAlert";
import CustomLoader from "../../../components/CustomLoader";
import CustomCheckbox from "../../../components/CustomCheckbox";
import CancelReservationModal from "../../../components/reservation/Modals/CancelReservationModal";
import { useTranslation } from "react-i18next";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { enqueueSnackbar } from "notistack";
import SnackbarConfig from "../../../utils/snackbarConfig";
import { useForm } from "react-hook-form";
import { stateListRequest } from "../../../redux/signup/action";
import { yupResolver } from "@hookform/resolvers/yup";
import "./styles.css";
import ConfirmationDialog from "../../../components/ConfirmationDialog";

const braintreeContainerId = "dropin-container";

const secondaryStyles = {
  backgroundColor: "#fff",
  borderColor: "#8A8886",
  color: "#605E5C",
  ":hover": { backgroundColor: "transparent" },
};

const hidePaylater = true;

const MakePayment = () => {
  const lgUp = useResponsive("up", "lg");
  const stateList = storePersist.get(localConstant.STATE_LIST);
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPE.DBT_CRED);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      routingNumber: "",
      accountNumber: "",
      address: "",
      accountName: "",
      accountCountry: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
    },
    resolver: yupResolver(ACH_PAYMENT_SCHEMNA),
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const [deleteCardDialog, setDeleteCardDialog] = useState({
    isOpen: false,
    title: `${t("pages.deleteCard.key01")} ?`,
    content: `${t("pages.deleteCard.key02")}`,
    reset: () => setDeleteCardDialog((prev) => ({ ...prev, isOpen: false })),
    handleContinue: undefined,
    isLoadingDailog: false,
  });
  const goHome = () => navigate("/ownerDashboard");

  const handleChangeAccrodian = (panel) => (event, newExpanded) => {
    setPaymentType(newExpanded ? panel : false);
  };

  const { paymentToken, isLoading } = useSelector(
    (state) => state.paymentsReducer,
  );
  const { state } = location;

  const customerId = storePersist.get(localConstant.ENTITY_CUSTOMER_ID);

  const isDaily = state?.reservationType === "DAILY";

  const [checked, setChecked] = useState({
    autoPayEnabled: true,
    savePaymentMethod: true,
  });

  const handleAutoPayClick = () =>
    setChecked((prev) => ({
      savePaymentMethod: true,
      autoPayEnabled: !prev.autoPayEnabled,
    }));

  const handleSavePaymentClick = () => {
    if (!isDaily && checked.autoPayEnabled) {
      setChecked((prev) => ({
        ...prev,
        savePaymentMethod: true,
      }));
      return;
    }
    setChecked((prev) => ({
      ...prev,
      savePaymentMethod: !prev.savePaymentMethod,
    }));
  };

  const myRef = useRef({});
  const [isInitialising, setInitialising] = useState(false);
  const [loading, setLoading] = useState(false);
  const [instance, setInstance] = useState(null);
  const [billing, setBilling] = useState({ totalAmount: 0, billNums: [] });
  const [dialogProps, setDialogProps] = useState({
    step: 0,
    isOpen: false,
    reason: "",
    isReservation: state.isReserve,
    onCancel: () =>
      setDialogProps((prev) => ({
        ...prev,
        isOpen: false,
        step: 0,
        isReservation: state?.isReserve,
      })),
  });
  const [alertProps, setAlertProps] = useState({
    isOpen: false,
    title: "",
    content: "",
    onClick: () => {
      setAlertProps((prev) => ({ ...prev, isOpen: false }));
      goHome();
    },
  });

  useEffect(() => {
    dispatch(fetchPaymentToken(state?.customerId || customerId));
    dispatch(stateListRequest({}, () => {}));
  }, []);

  useEffect(() => {
    if (!state?.billing) {
      enqueueSnackbar("Failed to fetch Bill Amount", {
        variant: "error",
        autoHideDuration: 3000,
        action: SnackbarConfig,
      });
      return;
    }
    const { charges = [] } = state?.billing || {};

    const formattedCharges = charges.reduce(
      (acc, val) => {
        acc.totalAmount = (acc.totalAmount || 0) + val.totalAmount;
        acc.billNums = [
          ...(acc.billNums || []),
          ...(val.billNum ? [val.billNum] : val.billNums || []),
        ];
        return acc;
      },
      { totalAmount: 0, billNums: [] },
    );

    setBilling(formattedCharges);
  }, [state]);

  useEffect(() => {
    if (billing.totalAmount && paymentToken) {
      initializeBraintree(paymentToken, `#${braintreeContainerId}`, {
        onStart: () => setInitialising(true),
        onSuccess: (instance) => {
          setInstance(instance);
        },
        onDone: () => setInitialising(false),
      });
    }
  }, [paymentToken]);

  const getPaymentAlertProps = (condition, errorMessage) => {
    const title = `${t("pages.paymentAlertPopUp.text01")} ${
      condition
        ? t("pages.paymentAlertPopUp.text02")
        : t("pages.paymentAlertPopUp.text03")
    }`;
    const subTitle = `${t("pages.paymentAlertPopUp.text04")}: ${
      state?.reservationId || ""
    }`;
    const variant = condition ? "success" : "error";
    const content = condition
      ? t("pages.paymentAlertPopUp.text05")
      : errorMessage;
    return { title, subTitle, variant, content };
  };

  const getDeleteCardAlertProps = (condition, errorMessage) => {
    const title = `${t("pages.deleteCardAlertPopUp.text01")} ${
      condition
        ? t("pages.deleteCardAlertPopUp.text02")
        : t("pages.deleteCardAlertPopUp.text03")
    }`;
    const subTitle = "";
    const variant = condition ? "success" : "error";
    const content = condition
      ? t("pages.deleteCardAlertPopUp.text05")
      : errorMessage;
    return { title, subTitle, variant, content };
  };

  const getCancelAlertProps = (response) => {
    const condition = response.success ? true : false;
    const title = condition
      ? !state.isExtend
        ? t("pages.paymentCancelPopUp.title01")
        : t("pages.paymentCancelPopUp.title02")
      : t("pages.paymentCancelPopUp.title03");
    const subTitle = `${t("pages.paymentCancelPopUp.text01")}: ${
      state?.reservationId || ""
    }`;
    const variant = condition ? "success" : "error";
    const content = condition
      ? `${t("pages.paymentCancelPopUp.text02")} ${moment()?.format(
          MOMENT_CONSTANTS.date.fullMonthDay,
        )}`
      : response.error;

    return { title, variant, content, subTitle };
  };

  const getPaymentProps = () => {
    if (isDaily)
      return {
        savePaymentMethod: checked.savePaymentMethod,
        autoPayEnabled: false,
      };
    return {
      savePaymentMethod: checked.savePaymentMethod,
      autoPayEnabled: checked.autoPayEnabled,
    };
  };

  const dispatchPayment = (payload) =>
    dispatch(
      makePayment(
        {
          customerId: state?.customerId || customerId,
          reservationId: state?.reservationId,
          ...getPaymentProps(),
        },
        {
          requestType: "CREATE_PAYMENT",
          paymentCurrencyCd: "USD",
          payments: [
            {
              paymentModeCd: getPaymentType(payload?.type) || "CRDCARD",
              paymentAmount: billing.totalAmount?.toFixed(2),
              paymentAttributes: {
                nonce: payload.nonce,
                paymentGatewayCorrelationId: JSON.parse(payload.deviceData)
                  ?.correlation_id,
              },
              criteria: {
                billNums: billing.billNums,
              },
            },
          ],
        },
        (response, errorMessage = "") => {
          setAlertProps((prev) => ({
            ...prev,
            isOpen: true,
            ...getPaymentAlertProps(response, errorMessage),
          }));
        },
      ),
    );

  const submitDialog = () => {
    setDeleteCardDialog((props) => ({
      ...props,
      isOpen: true,
      handleContinue: () => {
        handleDeleteCard();
      },
    }));
  };

  const dispatchDeleteCard = (payload) =>
    dispatch(
      deleteCard(
        {
          customerId: state?.customerId || customerId,
        },
        { nonce: payload.nonce },
        (response, errorMessage = "") => {
          setAlertProps((prev) => ({
            ...prev,
            isOpen: true,
            ...getDeleteCardAlertProps(response, errorMessage),
          }));
        },
      ),
    );

  const handlePayment = () => {
    if (instance) {
      instance.requestPaymentMethod(function (err, payload) {
        if (err) {
          console.error("Error requesting payment method:", err);
        } else {
          console.log({ payload });
          dispatchPayment(payload);
        }
      });
    }
  };

  const handleDeleteCard = () => {
    if (instance) {
      instance.requestPaymentMethod(function (err, payload) {
        if (err) {
          console.error("Error requesting payment method:", err);
        } else {
          console.log({ payload });
          dispatchDeleteCard(payload);
        }
      });
    }
  };

  const handleCancelReservation = () => {
    setDialogProps({
      step: 1,
      isOpen: true,
      isReservation: state?.isReserve,
      onCancel: () =>
        setDialogProps((prev) => ({
          ...prev,
          isOpen: false,
          step: 0,
          isReservation: state?.isReserve,
        })),
      onSubmit: () =>
        dispatch(
          cancelReservationRequest(
            {
              reservationId: state?.reservationId,
              extension: !!state?.isExtend,
            },
            {},
            (response) => {
              setLoading(false);
              setAlertProps((prev) => ({
                ...prev,
                isOpen: true,
                ...getCancelAlertProps(response),
              }));
            },
          ),
        ),
    });
  };

  const onSubmitACHPayment = (values) => {
    dispatch(
      makeACHPayment(
        state?.customerId || customerId,
        {
          requestType: "CREATE_PAYMENT",
          paymentCurrencyCd: "USD",
          payments: [
            {
              paymentModeCd: "EPAY",
              paymentAmount: billing.totalAmount?.toFixed(2),
              paymentAttributes: {
                partyShortName: values.name,
                name: values.name,
                bankAccountNumber: values.accountNumber,
                bankRoutingNumber: values.routingNumber,
                bankAccountName: values.accountName,
                bankCountry: values.accountCountry,
                addrLine1: values.address,
                city: values.city,
                state: values.state,
                zip: values.zipCode,
                country: values.country,
              },
              criteria: {
                billNums: billing.billNums,
              },
            },
          ],
        },
        (response) => {
          setAlertProps((prev) => ({
            ...prev,
            isOpen: true,
            ...getPaymentAlertProps(response),
          }));
        },
      ),
    );
  };

  return (
    <div
      style={{
        width: lgUp ? `calc(100% - ${240}px)` : "100%",
        marginLeft: lgUp ? "240px" : 0,
      }}
    >
      <NavBar
        myRef={myRef}
        enableBack={false}
        title={t("pages.makePayment.pageHeading")}
      />
      <MenuBar myRef={myRef} />
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <form onSubmit={handleSubmit(onSubmitACHPayment)}>
          <List
            sx={{
              bgcolor: "background.paper",
              width: "100%",
              overflow: "auto",
              height: "100%",
              maxHeight: "85vh",
            }}
          >
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              margin={"20px 20px"}
            >
              <Typography
                variant="h5"
                color={"primary.secondary"}
                textTransform={"capitalize"}
              >
                {t("pages.makePayment.text01")} $
                {billing?.totalAmount?.toFixed(2) || 0}
              </Typography>
            </Box>
            <Divider />
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"center"}
              margin={"10px 10px"}
              flexDirection={"column"}
              paddingBottom={"100px"}
            >
              {/* Commented this code as ACH moved to 1.4.0 */}

              {/* <Typography
                fontWeight={"bold"}
                fontSize={"15px"}
                marginTop={2}
                marginBottom={2}
                color="#1d5ab9"
              >
                Select Payment Method
              </Typography> */}

              <Grid width={{ xs: "80vw", md: "45vw" }}>
                <div id={braintreeContainerId} />
                {instance && (
                  <div>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <FormControlLabel
                          control={
                            <CustomCheckbox
                              value={t("pages.makePayment.text06")}
                              color="primary"
                              checked={checked.savePaymentMethod}
                              onChange={handleSavePaymentClick}
                            />
                          }
                          label={
                            <Typography
                              style={{ color: "#323130" }}
                              variant="body2"
                              color="primary"
                            >
                              {t("pages.makePayment.text06")}
                            </Typography>
                          }
                        />
                        {!isDaily && (
                          <FormControlLabel
                            control={
                              <CustomCheckbox
                                value={t("pages.makePayment.text07")}
                                color="primary"
                                checked={checked.autoPayEnabled}
                                onChange={handleAutoPayClick}
                              />
                            }
                            label={
                              <Typography
                                style={{ color: "#323130" }}
                                variant="body2"
                                color="primary"
                              >
                                {t("pages.makePayment.text07")}
                              </Typography>
                            }
                          />
                        )}
                      </Stack>
                      <Typography
                        onClick={() => submitDialog()}
                        sx={{
                          fontSize: 17,
                          color: "red",
                          textDecoration: "underline",
                          cursor: "pointer",
                          "&:hover": {
                            color: "darkblue",
                          },
                        }}
                      >
                        {t("pages.makePayment.text08")}
                      </Typography>
                    </Stack>
                  </div>
                )}
                {(isInitialising || !instance) && (
                  <Card
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    {(isLoading || isInitialising) && !instance ? (
                      <Typography sx={{ padding: "10px 0" }}>
                        {t("pages.makePayment.text02")}
                      </Typography>
                    ) : (
                      !instance && (
                        <Typography sx={{ padding: "10px 0", color: "red" }}>
                          {t("pages.makePayment.text03")}
                        </Typography>
                      )
                    )}
                  </Card>
                )}
              </Grid>

              {/* Commented this code as ACH moved to 1.4.0 */}

              {/* <Accordion
                expanded={paymentType === PAYMENT_TYPE.DBT_CRED}
                onChange={handleChangeAccrodian(PAYMENT_TYPE.DBT_CRED)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography fontWeight={"bold"}>
                    {" "}
                    Credit and Debit Card
                  </Typography>
                </AccordionSummary>
                <AccordionDetails></AccordionDetails>
              </Accordion> */}

              {/* <Accordion
                expanded={paymentType === PAYMENT_TYPE.ACH}
                onChange={handleChangeAccrodian(PAYMENT_TYPE.ACH)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1-content"
                  id="panel1-header"
                >
                  <Typography fontWeight={"bold"}> ACH Payment</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid width={{ xs: "80vw", md: "45vw" }}>
                    <Grid>
                      <Typography
                        fontWeight={"bold"}
                        fontSize={"15px"}
                        color="#1d5ab9"
                      >
                        Name
                        <span style={{ color: "red", marginLeft: "5px" }}>
                          *
                        </span>
                      </Typography>

                      <TextField
                        id="outlined-basic"
                        variant="outlined"
                        fullWidth
                        padding={0}
                        {...register("name")}
                        InputProps={{
                          className: "text-input-fields",
                        }}
                      />
                      {errors.name && (
                        <p
                          style={{
                            color: "red",
                            fontSize: "13px",
                            marginTop: "0px",
                          }}
                        >
                          {errors.name.message}
                        </p>
                      )}
                    </Grid>
                    <Grid
                      display={"flex"}
                      justifyContent={"space-between"}
                      flexDirection={{ xs: "column", md: "row" }}
                      marginTop={3}
                    >
                      <Grid width={{ xs: "100%", md: "45%" }}>
                        <Typography
                          fontWeight={"bold"}
                          fontSize={"15px"}
                          color="#1d5ab9"
                        >
                          Routing Number
                          <span style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </span>
                        </Typography>
                        <TextField
                          id="outlined-basic"
                          variant="outlined"
                          fullWidth
                          {...register("routingNumber")}
                          InputProps={{
                            className: "text-input-fields",
                          }}
                        />
                        {errors.routingNumber && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "13px",
                              marginTop: "0px",
                            }}
                          >
                            {errors.routingNumber.message}
                          </p>
                        )}
                      </Grid>
                      <Grid
                        width={{ xs: "100%", md: "45%" }}
                        marginTop={{ xs: 3, md: 0 }}
                      >
                        <Typography
                          fontWeight={"bold"}
                          fontSize={"15px"}
                          color="#1d5ab9"
                        >
                          Account Number
                          <span style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </span>
                        </Typography>
                        <TextField
                          id="outlined-basic"
                          variant="outlined"
                          fullWidth
                          {...register("accountNumber")}
                          InputProps={{
                            className: "text-input-fields",
                          }}
                        />
                        {errors.accountNumber && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "13px",
                              marginTop: "0px",
                            }}
                          >
                            {errors.accountNumber.message}
                          </p>
                        )}
                      </Grid>
                    </Grid>

                    addressLine 

                    <Grid marginTop={3}> addressLine
                      <Typography
                        fontWeight={"bold"}
                        fontSize={"15px"}
                        color="#1d5ab9"
                      >
                        Address Line
                        <span style={{ color: "red", marginLeft: "5px" }}>
                          *
                        </span>
                      </Typography>
                      <TextField
                        id="outlined-basic"
                        variant="outlined"
                        fullWidth
                        {...register("address")}
                        InputProps={{
                          className: "text-input-fields",
                        }}
                      />
                      {errors.address && (
                        <p
                          style={{
                            color: "red",
                            fontSize: "13px",
                            marginTop: "0px",
                          }}
                        >
                          {errors.address.message}
                        </p>
                      )}
                    </Grid>

                    <Grid
                      display={"flex"}
                      justifyContent={"space-between"}
                      flexDirection={{ xs: "column", md: "row" }}
                      marginTop={3}
                    >
                      <Grid width={{ xs: "100%", md: "45%" }}>
                        <Typography
                          fontWeight={"bold"}
                          fontSize={"15px"}
                          color="#1d5ab9"
                        >
                          Account Name
                          <span style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </span>
                        </Typography>
                        <TextField
                          id="outlined-basic"
                          variant="outlined"
                          fullWidth
                          {...register("accountName")}
                          InputProps={{
                            className: "text-input-fields",
                          }}
                        />
                        {errors.accountName && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "13px",
                              marginTop: "0px",
                            }}
                          >
                            {errors.accountName.message}
                          </p>
                        )}
                      </Grid>
                      <Grid
                        width={{ xs: "100%", md: "45%" }}
                        marginTop={{ xs: 3, md: 0 }}
                      >
                        <Typography
                          fontWeight={"bold"}
                          fontSize={"15px"}
                          color="#1d5ab9"
                        >
                          Account Country
                          <span style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </span>
                        </Typography>
                        <TextField
                          id="outlined-basic"
                          variant="outlined"
                          fullWidth
                          {...register("accountCountry")}
                          InputProps={{
                            className: "text-input-fields",
                          }}
                        />
                        {errors.accountCountry && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "13px",
                              marginTop: "0px",
                            }}
                          >
                            {errors.accountCountry.message}
                          </p>
                        )}
                      </Grid>
                    </Grid>

                    city state  

                    <Grid
                      display={"flex"}
                      justifyContent={"space-between"}
                      flexDirection={{ xs: "column", md: "row" }}
                      marginTop={3}
                    >
                      <Grid width={{ xs: "100%", md: "45%" }}>
                        <Typography
                          fontWeight={"bold"}
                          fontSize={"15px"}
                          color="#1d5ab9"
                        >
                          City
                          <span style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </span>
                        </Typography>
                        <TextField
                          id="outlined-basic"
                          variant="outlined"
                          fullWidth
                          {...register("city")}
                          InputProps={{
                            className: "text-input-fields",
                          }}
                        />
                        {errors.city && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "13px",
                              marginTop: "0px",
                            }}
                          >
                            {errors.city.message}
                          </p>
                        )}
                      </Grid>
                      <Grid
                        width={{ xs: "100%", md: "45%" }}
                        marginTop={{ xs: 3, md: 0 }}
                      >
                        <Typography
                          fontWeight={"bold"}
                          fontSize={"15px"}
                          color="#1d5ab9"
                        >
                          State
                          <span style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </span>
                        </Typography>
                        <Select
                          labelId="demo-multiple-name-label"
                          id="demo-multiple-name"
                          input={<OutlinedInput label="Name" />}
                          style={{
                            width: "100%",
                            height: "40px",
                          }}
                          {...register("state", {
                            disabled: watch("country") === "",
                          })}
                        >
                          {watch("country") !== "" &&
                            stateList[watch("country")].map((name) => (
                              <MenuItem key={name.code} value={name.code}>
                                {name.description}
                              </MenuItem>
                            ))}
                        </Select>
                        {errors.state && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "13px",
                              marginTop: "0px",
                            }}
                          >
                            {errors.state.message}
                          </p>
                        )}
                      </Grid>
                    </Grid>

                     zip country 
                    <Grid
                      display={"flex"}
                      justifyContent={"space-between"}
                      flexDirection={{ xs: "column", md: "row" }}
                      marginTop={3}
                    >
                      <Grid width={{ xs: "100%", md: "45%" }}>
                        <Typography
                          fontWeight={"bold"}
                          fontSize={"15px"}
                          color="#1d5ab9"
                        >
                          ZIP
                          <span style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </span>
                        </Typography>

                        <TextField
                          id="outlined-basic"
                          variant="outlined"
                          fullWidth
                          {...register("zipCode")}
                          InputProps={{
                            className: "text-input-fields",
                          }}
                        />
                        {errors.zipCode && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "13px",
                              marginTop: "0px",
                            }}
                          >
                            {errors.zipCode.message}
                          </p>
                        )}
                      </Grid>
                      <Grid
                        width={{ xs: "100%", md: "45%" }}
                        marginTop={{ xs: 3, md: 0 }}
                      >
                        <Typography
                          fontWeight={"bold"}
                          fontSize={"15px"}
                          color="#1d5ab9"
                        >
                          Country
                          <span style={{ color: "red", marginLeft: "5px" }}>
                            *
                          </span>
                        </Typography>
                        <Select
                          labelId="demo-multiple-name-label"
                          id="demo-multiple-name"
                          style={{
                            width: "100%",
                            height: "40px",
                          }}
                          {...register("country")}
                        >
                          {Object.keys(ACH_PAYMENT_COUNTRIES).map((name) => (
                            <MenuItem
                              key={name}
                              value={ACH_PAYMENT_COUNTRIES[name]}
                            >
                              {name}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.country && (
                          <p
                            style={{
                              color: "red",
                              fontSize: "13px",
                              marginTop: "0px",
                            }}
                          >
                            {errors.country.message}
                          </p>
                        )}
                      </Grid>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion> */}
            </Box>
          </List>

          {/* Actions Buttns for debit and credit */}
          <Box sx={{ mt: 5 }}>
            <Grid
              container
              spacing={1}
              width={"80%"}
              maxWidth={550}
              sx={{
                margin: "auto",
                left: 0,
                right: 0,
                position: "absolute",
                bottom: 10,
              }}
            >
              {!state?.hideCancel && (
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    sx={secondaryStyles}
                    onClick={handleCancelReservation}
                    variant="contained"
                    size="large"
                  >
                    {t("commonLabels.text02")}
                  </Button>
                </Grid>
              )}
              {/* {paymentType === PAYMENT_TYPE.DBT_CRED && instance && (
                <Grid item xs={6}>
                  <Button
                    sx={{
                      width: "100%",
                    }}
                    disabled={isLoading}
                    onClick={() => submitDialog()}
                    variant="contained"
                    size="large"
                  >
                    {t("pages.makePayment.text08")}
                  </Button>
                </Grid>
              )} */}
              {!hidePaylater && (
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    onClick={goHome}
                    variant="contained"
                    size="large"
                    sx={state?.hideCancel && secondaryStyles}
                  >
                    {t("pages.makePayment.text04")}
                  </Button>
                </Grid>
              )}

              <Grid
                item
                xs={
                  state?.hideCancel
                    ? hidePaylater
                      ? 12
                      : 6
                    : hidePaylater
                      ? 6
                      : 12
                }
              >
                {paymentType === PAYMENT_TYPE.DBT_CRED && instance && (
                  <Button
                    sx={{
                      width: "100%",
                    }}
                    disabled={isLoading}
                    onClick={handlePayment}
                    variant="contained"
                    size="large"
                  >
                    {t("pages.makePayment.text05")}
                  </Button>
                )}
                {paymentType === PAYMENT_TYPE.ACH && (
                  <Button
                    sx={{
                      width: "100%",
                    }}
                    disabled={isLoading}
                    type="submit"
                    variant="contained"
                    size="large"
                  >
                    {t("pages.makePayment.text05")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Box>
        </form>
      </div>
      {dialogProps.step === 1 && <CancelReservationModal {...dialogProps} />}
      {alertProps.isOpen && <CustomAlert {...alertProps} />}
      {(isLoading || loading) && <CustomLoader />}
      {deleteCardDialog.isOpen && <ConfirmationDialog {...deleteCardDialog} />}
    </div>
  );
};

export default MakePayment;
